import { FC, useCallback, useMemo, useState, useEffect } from "react";
import capitalize from "lodash/capitalize";
import {
  useQuery,
  useIsFetching,
  useQueryClient,
  useMutation,
} from "@tanstack/react-query";
import Select from "react-select";
import SportTableMobile from "../../contrainers/SportTable/SportTableMobile";
import BetTypeSelectorMobile from "../../contrainers/BetTypeSelector/BetTypeSelectorMobile";
import useScreen from "../../hooks/useScreen";
import BreadCrumb from "../../components/BreadCrumb";
import NewSportTableContainer from "./NewSportTableContainer";
import BetTypeSelector from "../BetTypeSelector";
import Refresh from "../../components/Refresh";
import { getSportQueryKey } from "../../utils/queryKeyHelper";
import query from "../../query";
import {
  SPORTS_WITHOUT_COMBO,
  SPORTS_WITHOUT_PALAY,
} from "../../constants/common";
import SearchInput from "../../components/SearchInput";
import SearchInputMobile from "../../components/SearchInputMobile";
import LeagueFilterModal from "../../contrainers/LeagueFilterModal";
import useLeagueFilterModal from "../../contrainers/LeagueFilterModal/useLeagueFilterModal";
import {
  LeagueFilterButtonMobile,
  LeagueFilterButton,
} from "../../components/LeagueFilterButton/LeagueFilterButtonMobile";
import { MatchSharePayload } from "../../types/table";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { providerOptions } from "../../pages/MyBets/config";
import mutation from "../../mutation";
import { CONFIG } from "../../config";

export type SportTableLiveAndTodayProps = {
  liveTitle: string;
  todayTitle: string;
  sport:
    | "football"
    | "basketball"
    | "volleyball"
    | "cricket"
    | "tabletennis"
    | "handball"
    | "icehockey"
    | "tennis"
    | "snooker"
    | "baseball"
    | "rugby"
    | "cricket"
    | "americanfootball"
    | "badminton"
    | "boxing"
    | "bull_fight"
    | "cock_fight";
};

export type SportTableMobileProps = {
  isBetTypeSelectorDisabled?: boolean;
};

const SportTableLiveAndToday: FC<
  SportTableLiveAndTodayProps & SportTableMobileProps
> = ({ liveTitle, todayTitle, sport, isBetTypeSelectorDisabled = false }) => {
  const { isDesktop } = useScreen();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [allTeamNames, setAllTeamNames] = useState<string[]>([]);
  const [liveLeagues, setLiveLeagues] = useState<
    { label: string; value: string }[]
  >([]);
  const [todayLeagues, setTodayLeagues] = useState<
    { label: string; value: string }[]
  >([]);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sharedMatchId = searchParams.get("matchId") || undefined;
  const { t } = useTranslation();

  const {
    isToggled: isLeagueModalOpen,
    toggle: toggleLeagueModal,
    isAllSelected,
    selectedLeagues,
    onSubmit: onLeagueFormSubmit,
    setFilterState,
  } = useLeagueFilterModal();

  const leagueOptions = useMemo(() => {
    const allLeagues = new Map<string, { label: string; value: string }>();

    todayLeagues.forEach((league) => {
      if (league && league.value) {
        allLeagues.set(league.value, league);
      }
    });

    liveLeagues.forEach((league) => {
      if (league && league.value) {
        allLeagues.set(league.value, league);
      }
    });

    return Array.from(allLeagues.values());
  }, [liveLeagues, todayLeagues]);

  useEffect(() => {
    setSearchTerm("");
    setAllTeamNames([]);
    setFilterState({ isAllSelected: true, selectedLeagues: [] });
  }, [sport, setFilterState]);

  const betTypeQuery = query.betType();
  const betType = useQuery(betTypeQuery.key, betTypeQuery.queryFn, {
    refetchOnMount: false,
    keepPreviousData: true,
  });

  const providerQuery = query.provider();
  const pvd = useQuery(providerQuery.key, providerQuery.queryFn, {
    refetchOnMount: false,
    keepPreviousData: true,
  });

  const updateBetTicketInfoPayload = useMutation(
    mutation.updateBetTicketInfoPayload,
    {
      onSuccess: (payload) => {
        queryClient.setQueryData(["betTicketInfoPayload"], payload);
      },
    },
  );

  const updateProvider = useMutation(mutation.updateProvider, {
    onSuccess: (payload) => {
      queryClient.setQueryData(["provider"], payload);
    },
  });

  const liveQueryKey = useMemo(
    () => getSportQueryKey(sport, "live", betType.data ?? "normal", pvd.data),
    [sport, betType.data, pvd.data],
  );
  const todayQueryKey = useMemo(
    () => getSportQueryKey(sport, "today", betType.data ?? "normal", pvd.data),
    [sport, betType.data, pvd.data],
  );

  const isFetchingLive = useIsFetching({
    queryKey: liveQueryKey,
  });

  const handleRefetchLive = useCallback(() => {
    queryClient.refetchQueries({ queryKey: liveQueryKey });
  }, [queryClient, liveQueryKey]);

  const isFetchingToday = useIsFetching({
    queryKey: todayQueryKey,
  });

  const handleRefetchToday = useCallback(() => {
    queryClient.refetchQueries({ queryKey: todayQueryKey });
  }, [queryClient, todayQueryKey]);

  const handleTeamsLoad = useCallback((teams: string[]) => {
    setAllTeamNames((prevTeams) => {
      const combined = new Set([...prevTeams, ...teams]);
      return Array.from(combined);
    });
  }, []);

  const handleShareMatch = useCallback(
    (match: MatchSharePayload) => {
      const title = `${match.homeName} vs ${match.awayName}`;
      const url = `${window.location.origin}/${sport}/${match.tableType}?matchId=${match.matchId}`;
      const text =
        match.kickoffTime ?
          `${title} ${t("share kickoff")} ${match.kickoffTime}\n${t(
            "share link",
          )}`
        : `${title}\n${t("shshare link")}`;

      if (navigator.share) {
        navigator.share({ title, text, url }).catch(console.error);
      } else {
        navigator.clipboard
          .writeText(`${text}\n${url}`)
          .then(() => alert(`${t("share copied")}`))
          .catch(() => alert(`${t("share copy_failed")}`));
      }
    },
    [sport, t],
  );

  if (isDesktop) {
    return (
      <>
        <div className="mb-10 desktop:ml-3 desktop:mr-3">
          <div className="w-full mx-auto">
            <div className="flex mb-4 items-center flex-wrap xl:flex-nowrap">
              <BreadCrumb path={liveTitle} />
              <div className="relative mr-3 py-2">
                <SearchInput
                  allItems={allTeamNames}
                  onSearchChange={setSearchTerm}
                />
              </div>
              <div className="relative mr-3">
                <Refresh
                  className="ml-auto border border-solid border-blue-500 text-blue-500 rounded flex justify-center items-center"
                  style={{
                    width: 150,
                    minHeight: 30,
                  }}
                  key="live"
                  onClick={handleRefetchLive}
                  isLoading={isFetchingLive > 0}
                  initialCounter={10}
                />
              </div>
              <BetTypeSelector
                sport={sport}
                isParlayDisabled={SPORTS_WITHOUT_PALAY.includes(sport)}
                isComboDisabled={SPORTS_WITHOUT_COMBO.includes(sport)}
              />
              <div className="relative" style={{ height: 30 }}>
                <LeagueFilterButton onClick={toggleLeagueModal} />
              </div>
            </div>
            {CONFIG.ENV === "develop" && (
              <div>
                <Select
                  options={providerOptions}
                  value={providerOptions.find(
                    (option) => pvd.data === option.value,
                  )}
                  isSearchable={false}
                  onChange={(option) => {
                    if (!option) {
                      return;
                    }
                    updateProvider.mutate(option.value);
                    updateBetTicketInfoPayload.mutate([]);
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex flex-col overflow-auto">
            <NewSportTableContainer
              key={sport}
              type="live"
              pvd={pvd.data}
              sport={sport}
              searchTerm={searchTerm}
              onTeamsLoad={handleTeamsLoad}
              leagueFilterState={{ isAllSelected, selectedLeagues }}
              onLeaguesLoad={setLiveLeagues}
              onShareMatch={handleShareMatch}
              sharedMatchId={sharedMatchId}
            />
          </div>
        </div>
        <div className="mb-10 desktop:ml-3 desktop:mr-3">
          <div className="w-full mx-auto">
            <div className="flex mb-4 items-center flex-wrap xl:flex-nowrap">
              <BreadCrumb path={todayTitle} />
              <div className="relative mr-3">
                <Refresh
                  className="ml-auto border border-solid border-blue-500 text-blue-500 rounded flex justify-center items-center"
                  style={{
                    width: 150,
                    minHeight: 30,
                  }}
                  key="today"
                  onClick={handleRefetchToday}
                  isLoading={isFetchingToday > 0}
                  initialCounter={60}
                />
              </div>
              <BetTypeSelector
                sport={sport}
                isParlayDisabled={SPORTS_WITHOUT_PALAY.includes(sport)}
                isComboDisabled={SPORTS_WITHOUT_COMBO.includes(sport)}
              />
            </div>
          </div>
          <div className="flex flex-col overflow-auto">
            {/* <SportTable title={todayTitle} type="today" sport={sport} /> */}
            <NewSportTableContainer
              type="today"
              pvd={pvd.data}
              sport={sport}
              searchTerm={searchTerm}
              onTeamsLoad={handleTeamsLoad}
              leagueFilterState={{ isAllSelected, selectedLeagues }}
              onLeaguesLoad={setTodayLeagues}
              onShareMatch={handleShareMatch}
              sharedMatchId={sharedMatchId}
            />
          </div>
        </div>
        <LeagueFilterModal
          isDesktop={isDesktop}
          key={sport}
          isToggled={isLeagueModalOpen}
          onClose={toggleLeagueModal}
          options={leagueOptions}
          defaultIsAllSelected={isAllSelected}
          defaultSelectedLeagues={selectedLeagues}
          onSubmit={(data) => {
            onLeagueFormSubmit(data);
          }}
        />
      </>
    );
  }

  return (
    <BetTypeSelectorMobile
      isBetTypeSelectorDisabled={isBetTypeSelectorDisabled}
      sport={sport}
      type="live-today"
      title={`${capitalize(sport)}/Live & Today`}>
      <div className="flex items-center gap-2 px-2 pb-4">
        <div className="flex-1">
          <SearchInputMobile
            allItems={allTeamNames}
            onSearchChange={setSearchTerm}
          />
        </div>
        <div>
          <LeagueFilterButtonMobile
            className="py-2.5"
            onClick={toggleLeagueModal}
          />
        </div>
      </div>

      {CONFIG.ENV === "develop" && (
        <div>
          <Select
            options={providerOptions}
            value={providerOptions.find((option) => pvd.data === option.value)}
            isSearchable={false}
            onChange={(option) => {
              if (!option) {
                return;
              }
              updateProvider.mutate(option.value);
              updateBetTicketInfoPayload.mutate([]);
            }}
          />
        </div>
      )}
      <div className="mb-10 desktop:ml-3 desktop:mr-3">
        <SportTableMobile
          title={liveTitle}
          type="live"
          sport={sport}
          searchTerm={searchTerm}
          onTeamsLoad={handleTeamsLoad}
          leagueFilterState={{ isAllSelected, selectedLeagues }}
          onLeaguesLoad={setLiveLeagues}
          onShareMatch={handleShareMatch}
          sharedMatchId={sharedMatchId}
        />
      </div>
      <div className="mb-10 desktop:ml-3 desktop:mr-3">
        <SportTableMobile
          title={todayTitle}
          type="today"
          sport={sport}
          searchTerm={searchTerm}
          onTeamsLoad={handleTeamsLoad}
          leagueFilterState={{ isAllSelected, selectedLeagues }}
          onLeaguesLoad={setTodayLeagues}
          onShareMatch={handleShareMatch}
          sharedMatchId={sharedMatchId}
        />
      </div>
      <LeagueFilterModal
        isDesktop={isDesktop}
        key={sport}
        isToggled={isLeagueModalOpen}
        onClose={toggleLeagueModal}
        options={leagueOptions}
        defaultIsAllSelected={isAllSelected}
        defaultSelectedLeagues={selectedLeagues}
        onSubmit={(data) => {
          onLeagueFormSubmit(data);
        }}
      />
    </BetTypeSelectorMobile>
  );
};

export default SportTableLiveAndToday;
